import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, ButtonGroup, Button, Spinner, Progress, Label, FormGroup, Input } from 'reactstrap';
import {
  FaChartLine,
  FaBoxOpen,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoice,
  FaFileInvoiceDollar,
  FaUndoAlt,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaCheckCircle,
  FaStore,
  FaShoppingCart
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saleActions } from '../../sagas/saleSlice';
import ReglementWidget from '../ReglementClient/ReglementWidget';
import { productActions } from '../../sagas/productSlice';
import './dashboard.css';
import { formatPrice } from '../../helpers/utility';
import DashboardLineChart from './DashboardLineChart';
import { addMonths, format } from 'date-fns';

const Dashboard = () => {
  // Date range filter for chart
  const current = new Date();
  const currentMonth = format(current, 'yyyy-MM-01');
  const nextMonthDate = addMonths(current, 1);
  const nextMonth = format(nextMonthDate, 'yyyy-MM-01');
  const previousMonthDate = addMonths(current, -1);
  const previousMonth = format(previousMonthDate, 'yyyy-MM-01');
  const [dateFrom, setDateFrom] = useState(previousMonth);
  const [dateTo, setDateTo] = useState(nextMonth);
  // Generate periods between dateFrom and dateTo
  const getPeriods = (from, to) => {
    const start = new Date(from);
    const end = new Date(to);
    const arr = [];
    let current = new Date(start);
    while (current <= end) {
      arr.push(format(current, 'yyyy-MM'));
      current = addMonths(current, 1);
    }
    return arr;
  };
  const periods = getPeriods(dateFrom, dateTo);
  // Fetch business report data from API
  const businessReport = useSelector(state => state.sales.businessReport) || [];

  // Extract periods and datasets from API data
  const chartPeriods = businessReport.map(item => item.month);
  const ventesData = businessReport.map(item => item.ventes);
  const achatsData = businessReport.map(item => item.achats);
  const depensesData = businessReport.map(item => item.depenses);
  const taxesData = businessReport.map(item => item.impots);
  const reglementsData = businessReport.map(item => item.reglements);

  const chartData = {
    labels: chartPeriods,
    datasets: [
      {
        label: 'Ventes',
        data: ventesData,
        borderColor: '#007bff',
        backgroundColor: 'rgba(0,123,255,0.1)',
        tension: 0.3,
      },
      {
        label: 'Achats',
        data: achatsData,
        borderColor: '#28a745',
        backgroundColor: 'rgba(40,167,69,0.1)',
        tension: 0.3,
      },
      {
        label: 'Dépenses',
        data: depensesData,
        borderColor: '#6c757d',
        backgroundColor: 'rgba(108,117,125,0.1)',
        tension: 0.3,
      },
      {
        label: 'Impots',
        data: taxesData,
        borderColor: '#ffc107',
        backgroundColor: 'rgba(255,193,7,0.1)',
        tension: 0.3,
      },
      {
        label: 'Réglements',
        data: reglementsData,
        borderColor: '#17a2b8',
        backgroundColor: 'rgba(23,162,184,0.1)',
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { title: { display: true, text: 'Période' } },
      y: { title: { display: true, text: 'Montant (DH)' }, beginAtZero: true },
    },
  };
  const dispatch = useDispatch();
  const {
    totalVentes,
    totalVentesDirect,
    totalAvoirs,
    totalAchats,
    totalFactureImpayees,
    totalAchatsImpayees,
    totalBenefice,
    chiffreAffaire,
    totalImpots,
    totalExpenses,
    totalRetourFournisseur,
    isLoading
  } = useSelector(state => state.sales);
  const produits = useSelector(state => state.products.products);
  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [filters, setFilters] = useState({
    dateFrom: formatDate(todayDate),
    dateTo: formatDate(tomorrowDate),
  });

  useEffect(() => {
    dispatch(saleActions.getSalesData({ ...filters }));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(saleActions.getBusinessReport({ dateFrom, dateTo }));
  }, [dateFrom, dateTo]);

  const { t } = useTranslation();

  useEffect(() => {
    dispatch(productActions.findAllUnites());
  }, []);

  // Helper function to get trend indicator
  const getTrendIndicator = (value, type = 'sales') => {
    const isPositive = value >= 0;
    return {
      icon: isPositive ? FaArrowUp : FaArrowDown,
      color: isPositive ? 'success' : 'danger',
      text: isPositive ? '+12%' : '-5%'
    };
  };

  return (
    <div className="dashboard-wrapper">
      <Container fluid className="p-2">

        {/* Date Filter for KPI Cards */}
        <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
          <div className="d-flex gap-2 align-items-center">

            <FormGroup className="mb-0">
              <Label for="dateFrom" className="mb-1">Date début</Label>
              <Input
                type="date"
                id="dateFrom"
                value={filters.dateFrom}
                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
              />
            </FormGroup>

            <FormGroup className="mb-0">
              <Label for="dateTo" className="mb-1">Date fin</Label>
              <Input
                type="date"
                id="dateTo"
                value={filters.dateTo}
                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
              />
            </FormGroup>

          </div>
        </div>

        {/* Main Content Grid */}
        <Row className="g-2">
          {/* KPI Cards - Sales */}
          <Col xxl={12} xl={12} lg={12}>
            <div className="kpi-section mb-3">
              <Row className="g-2">
                {[
                  {
                    title: t('dashboard.totalAllSales'),
                    value: formatPrice(totalVentes),
                    icon: FaFileInvoiceDollar,
                    color: 'primary',
                    bgColor: 'primary-subtle',
                  },
                  {
                    title: t('dashboard.totalDirectSales'),
                    value: formatPrice(totalVentesDirect),
                    icon: FaMoneyBillWave,
                    color: 'success',
                    bgColor: 'success-subtle',
                  },
                  {
                    title: t('dashboard.customerInvoiceNotPayed'),
                    value: formatPrice(totalFactureImpayees),
                    icon: FaExclamationTriangle,
                    color: 'warning',
                    bgColor: 'warning-subtle',
                  },
                  {
                    title: t('dashboard.totalSalesReturns'),
                    value: formatPrice(totalAvoirs),
                    icon: FaUndoAlt,
                    color: 'info',
                    bgColor: 'info-subtle',
                    trend: getTrendIndicator(0)
                  },
                  // --- Custom KPIs ---
                  
                  
                ].map((kpi, index) => (
                  <Col key={index} xxl={3} xl={3} lg={12} md={3} sm={12}>
                    <Card className={`kpi-card-compact h-100 border-0 shadow-sm bg-${kpi.bgColor}`}>
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className={`kpi-icon-compact rounded-circle bg-${kpi.color} p-2`}>
                            <kpi.icon size={16} className="text-white" />
                          </div>
                          
                        </div>
                        <div className="kpi-content">
                          <h5 className={`kpi-value mb-1 text-${kpi.color} fw-bold`}>{kpi.value}</h5>
                          <p className="kpi-title mb-0 text-muted small">{kpi.title}</p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            {/* KPI Cards - Purchases */}
            <div className="kpi-section mb-3">
              <Row className="g-2">
                {[
                  {
                    title: t('dashboard.numbreSupplierInvoices'),
                    value: formatPrice(totalAchats),
                    icon: FaChartLine,
                    color: 'secondary',
                    bgColor: 'secondary-subtle',
                  },
                  {
                    title: t('dashboard.supplierInvoiceNotPayed'),
                    value: formatPrice(totalAchatsImpayees),
                    icon: FaExclamationTriangle,
                    color: 'danger',
                    bgColor: 'danger-subtle',
                  },
                  {
                    title: t('dashboard.totalExpenses'),
                    value: formatPrice(totalExpenses),
                    icon: FaFileInvoiceDollar,
                    color: 'dark',
                    bgColor: 'dark-subtle',
                  },
                  {
                    title: t('dashboard.totalPurchaseReturns'),
                    value: formatPrice(totalRetourFournisseur),
                    icon: FaUndoAlt,
                    color: 'muted',
                    bgColor: 'light',
                  }
                ].map((kpi, index) => (
                  <Col key={index} xxl={3} xl={3} lg={12} md={3} sm={12}>
                    <Card className={`kpi-card-compact h-100 border-0 shadow-sm bg-${kpi.bgColor}`}>
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className={`kpi-icon-compact rounded-circle bg-${kpi.color} p-2`}>
                            <kpi.icon size={16} className="text-white" />
                          </div>
                        </div>
                        <div className="kpi-content">
                          <h5 className={`kpi-value mb-1 text-${kpi.color} fw-bold`}>{kpi.value}</h5>
                          <p className="kpi-title mb-0 text-muted small">{kpi.title}</p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
            {/* Special KPI Cards - Benefice, Chiffre d'Affaire, Impots */}
            {/* <div className="kpi-section mb-4">
              <Row className="g-4 justify-content-center">
                {[ 
                  {
                    title: t('dashboard.totalBenefice'),
                    value: formatPrice(totalBenefice),
                    icon: FaArrowUp,
                    color: 'success',
                    gradient: 'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)',
                    border: '3px solid #43e97b',
                  },
                  {
                    title: t('dashboard.chiffreAffaire'),
                    value: formatPrice(chiffreAffaire),
                    icon: FaChartLine,
                    color: 'primary',
                    gradient: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    border: '3px solid #667eea',
                  },
                  {
                    title: t('dashboard.totalImpots'),
                    value: formatPrice(totalImpots),
                    icon: FaFileInvoice,
                    color: 'warning',
                    gradient: 'linear-gradient(90deg, #f7971e 0%, #ffd200 100%)',
                    border: '3px solid #ffd200',
                  }
                ].map((kpi, index) => (
                  <Col key={index} xxl={4} xl={4} lg={12} md={4} sm={12}>
                    <Card
                      className="special-kpi-card h-100 border-0 shadow-lg"
                      style={{
                        background: kpi.gradient,
                        border: kpi.border,
                        color: '#fff',
                        minHeight: '160px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CardBody className="d-flex flex-column align-items-center justify-content-center">
                        <div className="mb-2">
                          <kpi.icon size={32} className="text-white" />
                        </div>
                        <h2 className={`mb-1 fw-bold`} style={{ fontSize: '2.5rem', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{kpi.value}</h2>
                        <p className="mb-0 text-white text-center" style={{ fontSize: '1.1rem', fontWeight: 500 }}>{kpi.title}</p>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div> */}
          </Col>
        </Row>
        {/* Line Chart Block */}
        <Card className="mb-4 shadow-sm border-0">
          <CardBody>
            <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
              <FormGroup className="mb-0">
                <Label for="startMonth" className="mb-1">{t('dashboard.startMonth')}</Label>
                <Input
                  type="month"
                  id="startMonth"
                  value={dateFrom.slice(0,7)}
                  onChange={e => setDateFrom(e.target.value + '-01')}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label for="endMonth" className="mb-1">{t('dashboard.endMonth')}</Label>
                <Input
                  type="month"
                  id="endMonth"
                  value={dateTo.slice(0,7)}
                  onChange={e => setDateTo(e.target.value + '-01')}
                />
              </FormGroup>
            </div>
            <DashboardLineChart data={chartData} options={chartOptions} />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Dashboard;
